import { createContext, useCallback, useContext, useEffect } from "react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Score } from "src/types";
import { Post } from "src/api/client-api/post/postSchema";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import { useQueryClient } from "react-query";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";

type PuckCompanyScoreContextValue = {
    isLoading: boolean;
    isError: boolean;
    scoreTitle: string | undefined;
    companyScores: Score[];
    companyPost?: Post | undefined;
    selectedVariation: { scoreId: number; variationId: number | null } | undefined;
    setVariation: (scoreId: number, variationId: number | null) => void;
    ssqId: number | undefined;
};

const PuckCompanyScoreContext = createContext<PuckCompanyScoreContextValue | null>(null);

type PuckCompanyScoreProviderProps = {
    children: ReactNode;
};

export const PuckCompanyScoreProvider = (props: PuckCompanyScoreProviderProps) => {
    const { children } = props;
    const { selectedCompany, currentCampaign, scores = [] } = useFoodpilotContext();
    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;
    const campaignId = currentCampaign?.id ? Number(currentCampaign.id) : undefined;
    const ssqId = selectedCompany?.organizationSector?.primarySsqId || selectedCompany?.organizationSector?.ssqId;

    const scoresParams = useOlapScoresParams({
        scores: {
            dimensions: [[["headings"]]],
        },
    });

    const queryClient = useQueryClient();
    const lastSelectedVariation = scoresParams.state.lastSelectedVariation;
    const lastSelectedScoreId = lastSelectedVariation?.scoreId ?? null;
    const lastSelectedVariationId = lastSelectedVariation?.variationId ?? null;
    const selectVariation = scoresParams.state.selectVariation;

    const {
        post: companyPost,
        isLoading: isPostLoading,
        error: isPostError,
    } = useGetOrganizationPost({
        companyId,
        ssqId,
        campaignId,
        scoresQuery: scoresParams.query,
    });

    const scoreTitle = scores.find((score) => score.id === lastSelectedVariation?.scoreId)?.title;

    useEffect(() => {
        if (isPostError) {
            queryClient.cancelQueries({ queryKey: ["getCompanyPost", companyId, campaignId] });
        }
    }, [isPostError, queryClient, companyId, campaignId]);

    const setVariation = useCallback(
        (scoreId: number, variationId: number | null) => {
            if (lastSelectedScoreId === scoreId && lastSelectedVariationId === variationId) {
                return;
            }

            selectVariation(scoreId, variationId);
        },
        [lastSelectedScoreId, lastSelectedVariationId, selectVariation],
    );

    const contextValue: PuckCompanyScoreContextValue = {
        isLoading: isPostLoading,
        isError: !!isPostError,
        companyScores: scores,
        companyPost,
        selectedVariation:
            scoresParams.state.lastSelectedVariation ?
                {
                    scoreId: scoresParams.state.lastSelectedVariation.scoreId,
                    variationId:
                        scoresParams.state.lastSelectedVariation.variationId !== null ?
                            scoresParams.state.lastSelectedVariation.variationId
                        :   null,
                }
            :   undefined,
        setVariation,
        scoreTitle,
        ssqId,
    };

    // // @https://github.com/Ilyomix: Implement a skeleton loader component for better look and feel
    // if (isPostLoading) {
    //     return (
    //         <Box sx={{ display: "flex", justifyContent: "center", p: theme.spacing(3) }}>
    //             <CircularProgress size={24} sx={{ mr: theme.spacing(2) }} />
    //         </Box>
    //     );
    // }

    // // @https://github.com/Ilyomix: Implement error handling matching the design system
    // if (isPostError || !companyId || !campaignId) {
    //     return <></>;
    // }

    return <PuckCompanyScoreContext.Provider value={contextValue}>{children}</PuckCompanyScoreContext.Provider>;
};

export const usePuckCompanyScore = () => {
    const context = useContext(PuckCompanyScoreContext);

    if (!context) {
        throw new Error("usePuckCompanyScore must be used within a PuckCompanyScoreProvider");
    }

    return context;
};
